// src/api/axiosClient.js
import axios from "axios";
import toast from "react-hot-toast";
import { IS_OFFLINE } from "./offline/isOffline";
import { createOfflineAdapter } from "./offline/offlineAdapter";

// BaseURL: priorize REACT_APP_API_BASE when explicitly set, otherwise always use relative '/api'.
// This lets CRA dev proxy route to backend locally and Vercel rewrites route in prod/tunnel.
const ENV_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/+$/, '');
const baseURL = ENV_BASE || '/api';

// Client "brut" sans interceptors — utilisé pour /token/ et /token/refresh
const raw = axios.create({ baseURL, withCredentials: true });

// Client principal
const api = axios.create({ baseURL, withCredentials: true });

// Offline mode: route all API calls to an in-browser mock adapter
if (IS_OFFLINE) {
  const adapter = createOfflineAdapter();
  raw.defaults.adapter = adapter;
  api.defaults.adapter = adapter;
}

/* ──────────────────────────────────────────────────────────────
   Helpers
────────────────────────────────────────────────────────────── */
// Sprint 2: tokens are now in httpOnly cookies - we no longer persist
// access/refresh in localStorage. clearTokens nettoie les vieux residus
// localStorage/sessionStorage qui auraient pu trainer cote client.
const clearTokens = () => {
  // Audit F-7: nettoyage complet des artefacts d'authentification.
  try { localStorage.removeItem("token"); } catch {}
  try { localStorage.removeItem("refresh"); } catch {}
  try { localStorage.removeItem("user"); } catch {}
  try { localStorage.removeItem("mfa_ticket"); } catch {}
  try { localStorage.removeItem("mfa_username"); } catch {}
  try { sessionStorage.clear(); } catch {}
};

// Lightweight client session identifier to help backend manage sessions
const ensureSessionId = () => {
  let sid = localStorage.getItem('session_id');
  if (!sid || sid === 'null' || sid === 'undefined') {
    // simple 32-hex generator
    sid = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
    localStorage.setItem('session_id', sid);
  }
  return sid;
};

/* ──────────────────────────────────────────────────────────────
   Request: Sprint 2 — tokens auto-sent via httpOnly cookies.
   We only attach the session ID header.
────────────────────────────────────────────────────────────── */
api.interceptors.request.use((config) => {
  try {
    const sid = ensureSessionId();
    if (sid) config.headers['X-Session-ID'] = sid;
  } catch {}
  return config;
});

/* ──────────────────────────────────────────────────────────────
   Expiration de session : redirection vers /login (hors pages publiques)
────────────────────────────────────────────────────────────── */
// Prefixes de routes publiques : un 401 declenche sur ces pages NE doit PAS
// rediriger (evite les boucles et les renvois inutiles vers /login).
export const PUBLIC_PATH_PREFIXES = [
  "/login", "/forgot-password", "/reset-password", "/verify-email",
  "/invite/", "/inscription", "/etablissement", "/demo", "/devis",
  "/questionnaire", "/ia/",
];

export function isPublicPath(pathname) {
  if (!pathname || pathname === "/") return true;
  return PUBLIC_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
}

export function buildLoginRedirect(pathname, search = "") {
  const current = (pathname || "/") + (search || "");
  return `/login?next=${encodeURIComponent(current)}`;
}

let _sessionExpiredHandled = false;
function handleSessionExpired() {
  try {
    const { pathname, search } = window.location;
    if (isPublicPath(pathname)) return; // page publique -> pas de redirection
    if (_sessionExpiredHandled) return; // une seule fois (evite toasts/redirections multiples)
    _sessionExpiredHandled = true;
    try { toast.error("Session expirée, veuillez vous reconnecter."); } catch {}
    window.location.assign(buildLoginRedirect(pathname, search));
  } catch {}
}

/* ──────────────────────────────────────────────────────────────
   Response: refresh automatique + rejouer les requêtes
────────────────────────────────────────────────────────────── */
let isRefreshing = false;
let queue = []; // requêtes en attente pendant le refresh

const resolveQueue = (error, newToken = null) => {
  queue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(newToken);
  });
  queue = [];
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error?.response?.status;

    if (status !== 401) return Promise.reject(error);

    if (original._retry) return Promise.reject(error);
    original._retry = true;

    // Sprint 2: refresh token is in httpOnly cookie — no need to check localStorage
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        queue.push({
          resolve: () => resolve(api(original)),
          reject,
        });
      });
    }

    try {
      isRefreshing = true;
      // Cookie is sent automatically via withCredentials
      await raw.post("/token/refresh/");
      resolveQueue(null);
      return api(original);
    } catch (refreshErr) {
      // Refresh impossible = session expiree. On nettoie, on previent
      // l'utilisateur et on redirige vers /login (sauf sur page publique).
      // Avant : les GET etaient rejoues sans auth via `raw` -> reponses non
      // authentifiees silencieuses, et jamais de redirection vers /login.
      resolveQueue(refreshErr, null);
      clearTokens();
      handleSessionExpired();
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;
export { raw };

/* ──────────────────────────────────────────────────────────────
   (Optionnel) Helpers d’auth côté front
────────────────────────────────────────────────────────────── */
export async function login(username, password) {
  const sid = ensureSessionId();
  const { data, status } = await raw.post('/token/', { username, password }, { validateStatus: () => true, headers: { 'X-Session-ID': sid } });
  if (status === 206 && data?.mfa_required) {
    return { mfa_required: true, username: data.username, ticket: data.ticket };
  }
  if (status >= 200 && status < 300) {
    // Sprint 2: tokens are now set as httpOnly cookies by the backend.
    // No localStorage write needed.
    return data;
  }
  const err = new Error('Login failed');
  err.response = { status, data };
  throw err;
}

export async function logout() {
  // Sprint 2: call server logout to clear httpOnly cookies + blacklist refresh
  try {
    await raw.post('/token/logout/');
  } catch {
    // best effort
  }
  clearTokens();
}

export async function verifyMfa(usernameOrTicket, codeOrUndefined, maybeTicket) {
  const sid = ensureSessionId();
  const username = usernameOrTicket;
  const code = codeOrUndefined;
  const ticket = maybeTicket;
  const payload = { username, code };
  if (ticket) payload.ticket = ticket;
  const { data } = await raw.post('/auth/mfa/totp/login/', payload, { headers: { 'X-Session-ID': sid } });
  // Sprint 2: tokens set as httpOnly cookies by backend — no localStorage write
  return data;
}
